import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { ControlledInput } from "../../../../components/controlled-input";
import { ModalTransferToUser } from "../../../../components/modal-transfer-to-user";
import { saleErrorMessage } from "../../../../helpers/sale-error-message";
import { useCreateAgencySaleDetail } from "../../../../hooks/agencies-sales-details/use-create-agency-sale-detail";
import { AGENCY_FIRST_QUERY_KEY } from "../../../../hooks/agencies/use-get-agency-first";
import { useGetMyProfile } from "../../../../hooks/profile/use-get-my-profile";
import {
  UserAction,
  UserActionReturn,
  USERS_ACTIONS,
} from "../../../../hooks/subscription/types";
import { useSub } from "../../../../hooks/subscription/use-sub";
import { InfoCoins } from "./components/info-coins";
import { SalesTotal } from "./components/sales-total";

const schema = z.object({
  id: z.string(),
  amount: z.string(),
});

type FormData = z.infer<typeof schema>;

// Câmbio da calculadora em reais: R$ 1 = 6.400 moedas. Por enquanto fixo aqui;
// depois vem do banco. O campo de reais é só ajuda de digitação: a venda
// continua sendo pela quantidade de moedas.
const COINS_POR_REAL = 6400;

export const CoinsTransferArea = () => {
  const queryClient = useQueryClient();

  const [paymentType, setPaymentType] = useState<"user" | "agent" | null>(null);
  const [info, setInfo] = useState<FormData | null>(null);
  const [isModalConfirmVisible, setIsModalConfirmVisible] = useState(false);
  const [reais, setReais] = useState("");
  // Retorno da última venda, mostrado na própria página (ver handleSubmit).
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { user, invalidate } = useGetMyProfile();
  const { create, isLoading } = useCreateAgencySaleDetail();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const coins = user?.agency?.coinsForSale;

  const amount = form.watch("amount");

  // Digitou moedas na mão e não bate mais com os reais: o campo de reais mentiria.
  useEffect(() => {
    if (reais && amount !== String(Number(reais) * COINS_POR_REAL)) {
      setReais("");
    }
  }, [amount]);

  const handleReaisChange = (valor: string) => {
    // Só reais inteiros: sem casa decimal.
    const digitos = valor.replace(/\D/g, "");
    setReais(digitos);
    form.setValue(
      "amount",
      digitos ? String(Number(digitos) * COINS_POR_REAL) : "",
      { shouldDirty: true, shouldTouch: true, shouldValidate: true }
    );
  };

  const limparFormulario = () => {
    setReais("");
    form.setValue("id", "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    form.setValue("amount", "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  // A venda AGORA é aguardada e o retorno é lido. Antes o `create` era disparado
  // sem `await` e sem checar nada: o modal fechava, o formulário limpava e a
  // revenda via "sucesso" mesmo quando a API recusava — uma recarga que nunca
  // aconteceu parecia ter dado certo. O servidor devolve { status, error }, e a
  // regra passou a ser: só limpa o formulário se a venda REALMENTE saiu.
  const handleSubmit = form.handleSubmit(async (data) => {
    if (!user?.id) {
      return;
    }

    const quantidade = parseFloat(data?.amount);
    setFeedback(null);
    setIsModalConfirmVisible(false);

    // Checagem local só pra evitar ida ao servidor no caso óbvio. Quem MANDA é o
    // servidor: ele debita o estoque e credita o comprador na mesma instrução,
    // então ele é a única fonte confiável de "deu pra vender".
    if (quantidade > (coins ?? 0)) {
      setFeedback({
        type: "error",
        message: saleErrorMessage("agency_not_balance_coins"),
      });
      return;
    }

    try {
      const retorno = await create({
        agencyId: user?.id,
        coins: quantidade,
        userId: Number(data?.id),
        isAgency: paymentType === "agent",
      });

      if (retorno?.status === "success") {
        setFeedback({
          type: "success",
          message: `Venda concluída: ${quantidade} moedas enviadas para o ID ${data?.id}.`,
        });
        limparFormulario();
        return;
      }

      // Erro tratado pelo servidor. Formulário CONTINUA preenchido pra revenda
      // corrigir o que estiver errado sem redigitar tudo.
      setFeedback({ type: "error", message: saleErrorMessage(retorno?.error) });
    } catch {
      // Rede caiu / gateway fora. Sem resposta não dá pra afirmar que falhou,
      // mas dá pra afirmar o que importa: se não chegou, nada foi debitado.
      setFeedback({ type: "error", message: saleErrorMessage(null) });
    }
  });

  const fields = form.formState.dirtyFields;
  const isDirty = Object.keys(fields).length > 0;

  const disabled = !isDirty || !paymentType;

  const handleSelect = (type: "user" | "agent") => {
    setPaymentType(type);
  };

  const boxStyle = (type: "user" | "agent") => ({
    flex: 1,
    padding: "15px",
    border: `2px solid ${paymentType === type ? "#2196f3" : "#ccc"}`,
    borderRadius: "8px",
    textAlign: "center" as const,
    cursor: "pointer",
    background: "#fff",
    transition: "border-color 0.2s",
  });

  useSub<UserActionReturn>({
    endpoint: `${USERS_ACTIONS}/${user?.id}`,
    skip: !user?.id,
    onData: (data) => {
      console.log("chegou");
      if (data?.action === UserAction.updateBalance) {
        invalidate();

        queryClient.invalidateQueries({
          queryKey: [AGENCY_FIRST_QUERY_KEY],
          exact: false,
        });
      }

      if (data?.action === UserAction.updateAgencyBalance) {
        invalidate();

        queryClient.invalidateQueries({
          queryKey: [AGENCY_FIRST_QUERY_KEY],
          exact: false,
        });
      }
    },
  });

  return (
    <div style={{ background: "#f0f3fc", padding: "12px" }}>
      <SalesTotal />

      <div
        style={{ background: "#fff", padding: "16px", borderRadius: "10px" }}
      >
        <InfoCoins />

        <ControlledInput
          control={form.control}
          name="id"
          label="* Transfer To"
          type="number"
          placeholder="ID"
        />

        <div>
          <label style={{ fontSize: "14px", fontWeight: "bold" }}>
            Valor em reais (R$)
          </label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="R$ 0"
            value={reais}
            onChange={(e) => handleReaisChange(e.target.value)}
            style={{
              display: "block",
              width: "100%",
              padding: "10px",
              marginTop: "5px",
              background: "#f0f3fc",
              marginBottom: "4px",
              border: "1px solid #ccc",
              borderRadius: "5px",
            }}
          />
          <small
            style={{ display: "block", color: "#777", marginBottom: "15px" }}
          >
            R$ 1 = {COINS_POR_REAL.toLocaleString("pt-BR")} coins
          </small>
        </div>

        <ControlledInput
          control={form.control}
          name="amount"
          label="* Transfer Amount"
          type="number"
          placeholder="Please enter coins amount"
        />

        <label
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            display: "block",
            marginBottom: "10px",
          }}
        >
          * To His/Her
        </label>

        <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
          <div style={boxStyle("user")} onClick={() => handleSelect("user")}>
            <span
              style={{
                background: "#2196f3",
                color: "#fff",
                padding: "4px 8px",
                borderRadius: "12px",
              }}
            >
              User
            </span>

            <div style={{ marginTop: "5px", fontSize: "14px" }}>
              Coins account
            </div>
          </div>

          <div style={boxStyle("agent")} onClick={() => handleSelect("agent")}>
            <span
              style={{
                background: "#ff4081",
                color: "#fff",
                padding: "4px 8px",
                borderRadius: "12px",
              }}
            >
              Agent
            </span>

            <div style={{ marginTop: "5px", fontSize: "14px" }}>
              Agent account
            </div>
          </div>
        </div>

        <small style={{ color: "#777" }}>
          (Coins account is only for consumption)
        </small>

        {/* Retorno da venda. Fica na PÁGINA (não em alert) pra revenda poder ler
            com calma, conferir o valor e o ID, e corrigir sem perder o que digitou. */}
        {feedback && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: "15px",
              padding: "12px 14px",
              borderRadius: "8px",
              border: `1px solid ${
                feedback.type === "success" ? "#a5d6a7" : "#ef9a9a"
              }`,
              borderLeft: `4px solid ${
                feedback.type === "success" ? "#2e7d32" : "#c62828"
              }`,
              background: feedback.type === "success" ? "#edf7ed" : "#fdecea",
              color: feedback.type === "success" ? "#1b5e20" : "#b71c1c",
              fontSize: "14px",
              lineHeight: 1.45,
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
            }}
          >
            <strong style={{ flexShrink: 0 }}>
              {feedback.type === "success" ? "✓" : "!"}
            </strong>
            <span>{feedback.message}</span>
          </div>
        )}

        <ModalTransferToUser
          typePayment={paymentType}
          visible={isModalConfirmVisible}
          info={info}
          handleSubmit={handleSubmit}
          handleCloseModal={() => setIsModalConfirmVisible(false)}
        >
          <button
            disabled={disabled || isLoading}
            onClick={() => {
              setFeedback(null);
              setIsModalConfirmVisible(true);
              setInfo(form.getValues());
            }}
            style={{
              background: !disabled && !isLoading ? "#3f51b5" : "#bbbaba",
              width: "100%",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              padding: "12px",
              fontSize: "16px",
              borderRadius: "8px",
              marginTop: "20px",
            }}
          >
            {isLoading ? "Enviando..." : "Transfer"}
          </button>
        </ModalTransferToUser>
      </div>
    </div>
  );
};
